import * as React from "react";

import { XIcon } from "lucide-react";

import { asIcon, Icon, IconData } from "simplydo/react/ui/icon";
import { Input, type InputProps } from "simplydo/react/ui/input";
import { Popup } from "simplydo/react/ui/popup";

import { Button } from "../../button";
import type { DateFieldProps } from "../inputs/BaseInput";
import checkIE from "../lib/checkIE";
import checkMobile from "../lib/checkMobile";
import { invoke } from "../lib/utils";

type InputWithRefProps = InputProps & {
  clearable?: boolean;
  onClear?: (e: React.SyntheticEvent<HTMLElement>) => void;
  innerRef?: (node: HTMLInputElement | null) => void;
};

class InputWithRef extends React.Component<InputWithRefProps> {
  public render() {
    const {
      value,
      clearable,
      icon,
      onClear,
      innerRef,
      onFocus,
      onBlur,
      onClick,
      onMouseEnter,
      label: _label,
      ...rest
    } = this.props;

    const clearIcon = (
      <Button size="small" variant="ghost" onClick={onClear}>
        <Icon icon={XIcon} />
      </Button>
    );

    const resolvedIcon =
      value && clearable
        ? clearIcon
        : icon && icon !== true
          ? asIcon(icon)
          : undefined;

    return (
      <Input
        {...rest}
        onFocus={onFocus}
        onBlur={onBlur}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        icon={resolvedIcon}
        value={value}
        input={{ ref: innerRef }}
      />
    );
  }
}

export type InputViewProps = DateFieldProps & {
  /** Used for passing input dom node (input field or inline calendar) to parent component. */
  onMount: (e: HTMLElement) => void;
  /** Called after input field value has changed. */
  onChange: (
    e: React.SyntheticEvent<HTMLElement>,
    data: { value: string },
  ) => void;
  /** Called when component looses focus. */
  onBlur?: (e: React.SyntheticEvent<HTMLElement>) => void;
  closePopup: () => void;
  openPopup: () => void;
  /** Called when the input gains focus. */
  onFocus?: () => void;
  /** Called when the popup closes. */
  onClose?: () => void;
  /** Function for rendering picker. */
  renderPicker: () => React.ReactNode;
  /** Called after clear icon has clicked. */
  onClear?: (
    e: React.SyntheticEvent<HTMLElement>,
    data: { value: string },
  ) => void;
  /** Whether to close a popup when cursor leaves it. */
  closeOnMouseLeave?: boolean;
  /** A field can have its label next to instead of above it. */
  inlineLabel?: boolean;
  /** Using the clearable setting will let users remove their selection from a calendar. */
  clearable?: boolean;
  /** Optional Icon to display inside the Input. */
  icon?: IconData | boolean | false;
  /** Icon position. Default: 'right'. */
  iconPosition?: "left" | "right";
  /** Whether popup is closed. */
  popupIsClosed?: boolean;
  /** Whether to display inline picker or picker inside a popup. */
  inline?: boolean;
  /** Duration of the CSS transition animation in milliseconds. */
  duration?: number;
  /** Where to display popup. */
  popupPosition?:
    | "top left"
    | "top right"
    | "bottom right"
    | "bottom left"
    | "right center"
    | "left center"
    | "top center"
    | "bottom center";
  /** Currently selected value (always a display string). */
  value?: string;
  /** Picker width (any value that `style.width` can take). */
  pickerWidth?: string;
  /** Style object for picker. */
  pickerStyle?: object;
  /** Do not display popup if true. */
  readOnly?: boolean;
  /** Try to prevent mobile keyboard appearing. */
  hideMobileKeyboard?: boolean;
  children?: React.ReactNode;
};

class InputView extends React.Component<InputViewProps> {
  public static defaultProps = {
    inline: false,
    closeOnMouseLeave: true,
    tabIndex: 0,
    clearable: false,
    duration: 200,
    iconPosition: "right" as const,
  };

  private inputNode: HTMLElement | null | undefined;
  private popupNode: HTMLElement | null | undefined;
  private mouseLeaveTimeout: number | null = null;

  private isInsidePicker = (target: EventTarget | null): boolean => {
    if (!(target instanceof Node)) {
      return false;
    }
    return Boolean(
      this.popupNode?.contains(target) || this.inputNode?.contains(target),
    );
  };

  public onBlur = (e: React.FocusEvent<HTMLElement>, ...args: unknown[]) => {
    const { closePopup } = this.props;

    if (this.isInsidePicker(e.relatedTarget) || checkIE()) {
      return;
    }

    invoke(this.props, "onBlur", e, ...args);
    closePopup();
  };

  public onMouseLeave = (
    e: React.MouseEvent<HTMLElement>,
    ...args: unknown[]
  ) => {
    const { closeOnMouseLeave, closePopup } = this.props;

    if (!this.isInsidePicker(e.relatedTarget) && closeOnMouseLeave) {
      invoke(this.props, "onMouseLeave", e, ...args);
      this.mouseLeaveTimeout = window.setTimeout(() => {
        if (this.mouseLeaveTimeout) {
          closePopup();
        }
      }, 500);
    }
  };

  public onMouseEnter = (
    e: React.MouseEvent<HTMLElement>,
    ...args: unknown[]
  ) => {
    const { closeOnMouseLeave } = this.props;

    invoke(this.props, "onMouseEnter", e, ...args);
    if (
      e.currentTarget === this.popupNode ||
      e.currentTarget === this.inputNode
    ) {
      if (closeOnMouseLeave) {
        clearTimeout(this.mouseLeaveTimeout!);
        this.mouseLeaveTimeout = null;
      }
    }
  };

  public render() {
    const {
      renderPicker,
      popupPosition,
      inline,
      value,
      closeOnMouseLeave,
      onChange,
      onClear,
      children: _children,
      inlineLabel: _inlineLabel,
      popupIsClosed,
      tabIndex,
      onMount,
      closePopup: _closePopup,
      openPopup,
      duration: _duration,
      pickerWidth: _pickerWidth,
      pickerStyle: _pickerStyle,
      onClose: _onClose,
      iconPosition,
      icon,
      readOnly,
      hideMobileKeyboard,
      ...fieldRest
    } = this.props;

    const { label: _label, ...inputRest } = fieldRest;

    const inputElement = (
      <InputWithRef
        {...inputRest}
        // trying to use this hack https://stackoverflow.com/a/7610923 for hiding mobile keyboard
        readOnly={(checkMobile() && hideMobileKeyboard) || readOnly}
        icon={icon === false ? undefined : icon}
        iconPosition={
          icon && iconPosition !== "right" ? iconPosition : undefined
        }
        innerRef={(e) => {
          this.inputNode = e;
          if (e) {
            onMount(e);
          }
        }}
        value={value ?? ""}
        tabIndex={tabIndex}
        clearable={this.props.clearable}
        onClear={(e) => (onClear || onChange)(e, { ...inputRest, value: "" })}
        onFocus={() => {
          invoke(this.props, "onFocus");
          openPopup();
        }}
        onClick={() => {
          invoke(this.props, "onFocus");
          openPopup();
        }}
        onBlur={this.onBlur}
        onMouseEnter={this.onMouseEnter}
        onChange={(e, data) => onChange(e, { value: data.value })}
      />
    );

    if (inline) {
      return renderPicker();
    }

    return (
      <>
        {inputElement}
        {!readOnly && (
          <Popup
            open={!popupIsClosed}
            trigger={null}
            position={popupPosition}
            hoverable={closeOnMouseLeave}
            fluid
            transparent
            context={this.inputNode}
            on="hover"
          >
            <div
              onBlur={this.onBlur}
              onMouseLeave={this.onMouseLeave}
              onMouseEnter={this.onMouseEnter}
              style={{ outline: "none" }}
              tabIndex={0}
              ref={(ref) => {
                this.popupNode = ref;
              }}
            >
              {renderPicker()}
            </div>
          </Popup>
        )}
      </>
    );
  }

  public scrollListener = () => {
    const { closePopup } = this.props;
    closePopup();
  };

  private setScrollListener() {
    window.addEventListener("scroll", this.scrollListener);
  }

  private unsetScrollListener() {
    window.removeEventListener("scroll", this.scrollListener);
  }
}

export default InputView;
